import type { ReceiptOptions } from '@iws/pos-devices'
import { useQuery } from '@tanstack/react-query'

import { useSession } from '@/contexts/SessionContext'
import { meta } from '@/db/database'
import { DEFAULT_RETURN_WINDOW_DAYS, RETURN_WINDOW_DAYS_KEY } from '@/db/repositories/orders'

/** What every receipt prints above and below the sale; from the business profile, kept on the device for offline. */
export interface ReceiptHeader {
  name: string
  address?: string
  phone?: string
  email?: string
  registration_no?: string
  /** Which parts the receipt shows (`app_settings.pos.receipt`); unset keeps the defaults. */
  receipt?: ReceiptOptions | null
  /** Business `sst_id` (the VAT registration printed as BIN). */
  tax_id?: string
  return_window_days?: number | null
  /** `app_settings.pos.receipt_footer`; empty prints the default thanks. */
  footer?: string
}

export const RECEIPT_HEADER_KEY = 'settings.receipt_header'

const DEMO_HEADER: ReceiptHeader = {
  name: 'IWS Demo Mart',
  address: 'House 12, Road 5, Dhanmondi, Dhaka',
  tax_id: '001234567-0101',
  return_window_days: 7,
}

const readHeader = async (): Promise<ReceiptHeader> => ({
  ...((await meta.get<ReceiptHeader>(RECEIPT_HEADER_KEY)) ?? { name: '' }),
  return_window_days: (await meta.get<number | null>(RETURN_WINDOW_DAYS_KEY)) ?? DEFAULT_RETURN_WINDOW_DAYS,
})

export const receiptHeaderQueryKey = ['receipt-header'] as const

/** The demo API has no business profile, so the demo till prints a sample shop. */
export const useReceiptHeader = (): ReceiptHeader => {
  const { stage } = useSession()
  const demo = stage === 'demo'
  const { data } = useQuery({ queryKey: receiptHeaderQueryKey, queryFn: readHeader, enabled: !demo })
  return demo ? DEMO_HEADER : (data ?? { name: '' })
}
